/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: We use dangerouslySetInnerHTML for code highlighting */
'use client'

import { useRef, useEffect, useState } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { codeToHtml } from 'shiki'
import { InstallSnippet } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import type { HowItWorksStepCode } from './how-it-works-simple'

/** Single step in the vertical (connecting-line timeline) how-it-works layout. */
export interface HowItWorksVerticalStep {
  /** Step number displayed (e.g., "01", "02") */
  number: string
  /** Step title */
  title: string
  /** Step description — supports Markdown */
  description: string
  /** Optional code or install block below the description. Omit for a text-only step. */
  code?: HowItWorksStepCode
}

export interface HowItWorksVerticalProps {
  /** Small label above heading */
  label?: string
  /** Section heading */
  heading?: string
  /** Section description — supports Markdown */
  description?: string
  /** Array of steps (3-5 recommended) */
  steps?: HowItWorksVerticalStep[]
  className?: string
}

const defaultSteps: HowItWorksVerticalStep[] = [
  {
    number: '01',
    title: 'Install the package',
    description: 'Add MDXUI to your project with a single command. Click to copy!',
    code: { type: 'install', package: '@mdxui/primitives', command: 'pnpm add' },
  },
  {
    number: '02',
    title: 'Import components',
    description: 'Import the components you need from the library. Full TypeScript support included.',
    code: { type: 'code', code: "import { Hero, Features } from 'mdxui'", language: 'typescript' },
  },
  {
    number: '03',
    title: 'Build your page',
    description: 'Compose components together to create beautiful interfaces. Customize as needed.',
    code: { type: 'code', code: '<Hero title="Welcome" cta={{ label: "Start" }} />', language: 'tsx' },
  },
]

/** Inline code block with syntax highlighting - matches InstallSnippet height/styling */
function HighlightedCode({ code, language = 'typescript' }: { code: string; language?: string }) {
  const [darkHtml, setDarkHtml] = useState<string>('')
  const [lightHtml, setLightHtml] = useState<string>('')

  useEffect(() => {
    codeToHtml(code, { lang: language, theme: 'github-dark' }).then(setDarkHtml)
    codeToHtml(code, { lang: language, theme: 'github-light' }).then(setLightHtml)
  }, [code, language])

  return (
    <div className="mt-3 h-11 flex items-center overflow-x-auto rounded-[var(--radius)] bg-muted px-4 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent hover:[&::-webkit-scrollbar-thumb]:bg-border">
      <div
        className="text-sm [&_pre]:!m-0 [&_pre]:!bg-transparent [&_pre]:!whitespace-nowrap [&_pre]:!w-fit [&_code]:!bg-transparent dark:hidden"
        dangerouslySetInnerHTML={{ __html: lightHtml }}
      />
      <div
        className="hidden text-sm [&_pre]:!m-0 [&_pre]:!bg-transparent [&_pre]:!whitespace-nowrap [&_pre]:!w-fit [&_code]:!bg-transparent dark:block"
        dangerouslySetInnerHTML={{ __html: darkHtml }}
      />
    </div>
  )
}

/** Renders the optional code/install block for a step; nothing when absent. */
function StepCodeBlock({ code }: { code?: HowItWorksStepCode }) {
  if (!code) return null
  if (code.type === 'install') {
    return (
      <div className="mt-3">
        <InstallSnippet
          package={code.package}
          command={code.command || 'pnpm add'}
          variant="muted"
          className="w-full"
        />
      </div>
    )
  }
  return <HighlightedCode code={code.code} language={code.language} />
}

// Animation variants
const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1]

const lineVariants = {
  hidden: { scaleY: 0 },
  visible: { scaleY: 1, transition: { duration: 0.8, ease: easeOut } },
}

const nodeVariants = {
  hidden: { opacity: 0, scale: 0 },
  visible: (delay: number) => ({
    opacity: 1,
    scale: 1,
    transition: { delay, duration: 0.3, ease: easeOut },
  }),
}

const stepVariantsLeft = {
  hidden: { opacity: 0, x: -20 },
  visible: (delay: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay, duration: 0.5, ease: easeOut },
  }),
}

const stepVariantsRight = {
  hidden: { opacity: 0, x: 20 },
  visible: (delay: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay, duration: 0.5, ease: easeOut },
  }),
}

const PROSE =
  'prose prose-sm mt-2 max-w-none text-muted-foreground prose-strong:text-foreground prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none'

/**
 * HowItWorksVertical — alternating timeline with a connecting line.
 *
 * One prop-driven layout covering text-only, inline-code, and install steps
 * (formerly HowItWorks3 / HowItWorks3Code / HowItWorks3CodeInstall). Each step
 * may carry an optional `code` block (`type: 'code'` or `type: 'install'`); omit
 * it for a text-only step. Steps stack vertically on mobile and alternate
 * left/right around a centered line on desktop.
 *
 * @example
 * ```tsx
 * <HowItWorksVertical
 *   heading="Simple steps to get started"
 *   steps={[
 *     { number: '01', title: 'Install', description: 'Add the package', code: { type: 'install', package: 'mdxui', command: 'pnpm add' } },
 *     { number: '02', title: 'Import', description: 'Pull in components', code: { type: 'code', code: "import { Hero } from 'mdxui'" } },
 *     { number: '03', title: 'Build', description: 'Compose your page' },
 *   ]}
 * />
 * ```
 */
const HowItWorksVertical = ({
  label = 'How it works',
  heading = 'Simple steps to get started',
  description = 'Get up and running quickly with our streamlined process.',
  steps = defaultSteps,
  className,
}: HowItWorksVerticalProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('bg-background py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="mx-auto mb-16 flex max-w-2xl flex-col items-center gap-4 text-center"
          variants={headerAnimation}
        >
          <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
          <h2 className="text-3xl font-semibold sm:text-4xl text-balance">{heading}</h2>
          <div className="text-balance">
            <Markdown size="base">{description}</Markdown>
          </div>
        </motion.div>

        {/* Timeline */}
        <div className="relative mx-auto max-w-5xl">
          {/* Mobile layout: single column with line on left */}
          <div className="md:hidden">
            <motion.div
              className="absolute left-[4px] top-[22px] bottom-[5px] w-px origin-top bg-border"
              variants={prefersReducedMotion ? reducedMotionVariants : lineVariants}
            />
            <div className="flex flex-col gap-8">
              {steps.map((step, index) => {
                const delay = 0.3 + index * 0.15
                return (
                  <div key={step.number} className="relative flex items-start gap-4 sm:gap-6">
                    <motion.div
                      className="relative z-10 mt-[17px] size-[10px] shrink-0 rounded-full bg-primary"
                      variants={prefersReducedMotion ? reducedMotionVariants : nodeVariants}
                      custom={delay}
                    />
                    <motion.div
                      className="min-w-0 flex-1 pt-1 pb-2"
                      variants={prefersReducedMotion ? reducedMotionVariants : stepVariantsRight}
                      custom={delay}
                    >
                      <span className="text-3xl font-semibold leading-none text-muted-foreground/40">
                        {step.number}
                      </span>
                      <h3 className="mt-3 text-lg font-semibold">{step.title}</h3>
                      <div className={PROSE}>
                        <ReactMarkdown>{step.description}</ReactMarkdown>
                      </div>
                      <StepCodeBlock code={step.code} />
                    </motion.div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Desktop layout: alternating cards with centered number + dot */}
          <div className="hidden md:block">
            <motion.div
              className="absolute left-1/2 top-[5px] bottom-[5px] w-px -translate-x-1/2 origin-top bg-border"
              variants={prefersReducedMotion ? reducedMotionVariants : lineVariants}
            />
            <div className="flex flex-col gap-10">
              {steps.map((step, index) => {
                const isLeft = index % 2 === 0
                const delay = 0.3 + index * 0.15
                return (
                  <div key={step.number} className="grid grid-cols-[1fr_48px_1fr] items-start">
                    {isLeft ? (
                      <motion.div
                        className="flex items-start justify-end"
                        variants={prefersReducedMotion ? reducedMotionVariants : stepVariantsLeft}
                        custom={delay}
                      >
                        <div className="w-full max-w-[480px] rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-muted/30 p-5">
                          <span className="text-3xl font-semibold text-muted-foreground/40">{step.number}</span>
                          <h3 className="mt-2 text-lg font-semibold">{step.title}</h3>
                          <div className={PROSE}>
                            <ReactMarkdown>{step.description}</ReactMarkdown>
                          </div>
                          <StepCodeBlock code={step.code} />
                        </div>
                      </motion.div>
                    ) : (
                      <div />
                    )}

                    <div className="relative flex justify-center pt-[33px]">
                      <motion.div
                        className={cn(
                          'absolute top-[38px] h-px w-3 bg-border',
                          isLeft ? 'right-[calc(50%+5px)]' : 'left-[calc(50%+5px)]',
                        )}
                        variants={prefersReducedMotion ? reducedMotionVariants : nodeVariants}
                        custom={delay}
                      />
                      <motion.div
                        className="relative z-10 size-[10px] shrink-0 rounded-full bg-primary"
                        variants={prefersReducedMotion ? reducedMotionVariants : nodeVariants}
                        custom={delay}
                      />
                    </div>

                    {!isLeft ? (
                      <motion.div
                        className="flex items-start justify-start"
                        variants={prefersReducedMotion ? reducedMotionVariants : stepVariantsRight}
                        custom={delay}
                      >
                        <div className="w-full max-w-[480px] rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-muted/30 p-5">
                          <span className="text-3xl font-semibold text-muted-foreground/40">{step.number}</span>
                          <h3 className="mt-2 text-lg font-semibold">{step.title}</h3>
                          <div className={PROSE}>
                            <ReactMarkdown>{step.description}</ReactMarkdown>
                          </div>
                          <StepCodeBlock code={step.code} />
                        </div>
                      </motion.div>
                    ) : (
                      <div />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  )
}

export { HowItWorksVertical }
